// AgriLinkAgriTourismHub.jsx
import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faLeaf,
  faCalendarAlt,
  faUtensils,
  faGraduationCap,
  faSpa,
  faShoppingBasket,
  faHeadset,
  faMapMarkerAlt,
  faRupeeSign,
  faStar,
  faUsers,
  faTicketAlt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

/**
 * AgriLinkAgriTourismHub
 * - Hub page that shows 8 portlets (use cases).
 * - Each portlet expands in-place and renders a working dummy sub-component.
 *
 * Drop into your MERN React app. Replace dummy logic with backend later.
 */

const Portlet = ({ icon, title, desc, expanded, onToggle, children }) => {
  return (
    <div className="bg-gray-600 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col items-center p-4"> 
      <button
        onClick={onToggle}
        className="py-4 px-6 flex flex-col items-center gap-3 rounded-xl bg-green-700 hover:bg-green-800 transition w-auto"
        aria-expanded={expanded}
      >
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-xl">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h3 className="text-lg font-semibold text-yellow-200">{title}</h3>
              <p className="text-sm text-white mt-1">{desc}</p>
            </div>
            <div className="text-xs text-orange-300 ">{expanded ? "Collapse" : "Open"}</div>
          </div>
        </div>
      </button>
      {expanded && <div className="p-5 border-t border-gray-100 bg-green-50/40">{children}</div>}
    </div>
  );
};

/* ----------------- Subcomponents (dummy / functional) ----------------- */

/* 1. Farm Stay Booking (re-usable from earlier) */
const FarmStayBookingPortlet = () => {
  const [selectedStay, setSelectedStay] = useState(null);
  const [showSuccess, setShowSuccess] = useState("");

  const farmstays = [
    {
      id: 1,
      name: "Green Valley Farmstay",
      location: "Nashik, Maharashtra",
      price: 2500,
      rating: 4.6,
      image:
        "https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=800&q=60",
      desc: "Vineyards, tractor rides and farm-to-table meals.",
    },
    {
      id: 2,
      name: "Rustic Roots Eco Farm",
      location: "Wayanad, Kerala",
      price: 3800,
      rating: 4.8,
      image:
        "https://images.unsplash.com/photo-1589987607627-4db6c6b5d054?w=800&q=60",
      desc: "Cottages, spice walks, and wellness sessions.",
    },
    {
      id: 3,
      name: "Sunrise Orchard Retreat",
      location: "Nakuru, Kenya",
      price: 3000,
      rating: 4.5,
      image:
        "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=60",
      desc: "Apple orchards and nature trails.",
    },
  ];

  const [form, setForm] = useState({
    name: "",
    email: "",
    checkIn: "",
    checkOut: "",
    guests: 1,
  });

  const startBooking = (stay) => {
    setSelectedStay(stay);
    setShowSuccess("");
    setForm({ name: "", email: "", checkIn: "", checkOut: "", guests: 1 });
  };

  const submitBooking = (e) => {
    e.preventDefault();
    setShowSuccess(`Booking request sent for ${selectedStay.name}. We will contact you at ${form.email}.`);
    setSelectedStay(null);
  };

  return (
    <div>
      {showSuccess && (
        <div className="p-3 rounded-md bg-green-100 border border-green-200 text-green-800 mb-4">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          {showSuccess}
        </div>
      )}

      {!selectedStay ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {farmstays.map((s) => (
            <div key={s.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src={s.image} alt={s.name} className="w-full h-40 object-cover" />
              <div className="p-3">
                <h4 className="font-semibold text-gray-800">{s.name}</h4>
                <p className="text-sm text-gray-500">{s.location}</p>
                <p className="mt-2 text-sm text-gray-700">{s.desc}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 text-sm text-yellow-600">
                    <FontAwesomeIcon icon={faStar} /> {s.rating}
                  </div>
                  <div className="text-green-700 font-semibold">
                    <FontAwesomeIcon icon={faRupeeSign} /> {s.price}/night
                  </div>
                </div>
                <button
                  onClick={() => startBooking(s)}
                  className="mt-3 w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg"
                >
                  Book
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h4 className="font-semibold text-gray-800 mb-2">Booking: {selectedStay.name}</h4>
          <form onSubmit={submitBooking} className="space-y-3">
            <input
              required
              name="name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Your name"
              className="w-full p-2 border rounded"
            />
            <input
              required
              name="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="Email"
              className="w-full p-2 border rounded"
            />
            <div className="grid grid-cols-2 gap-2">
              <input required name="checkIn" type="date" value={form.checkIn} onChange={(e)=>setForm(p=>({...p, checkIn:e.target.value}))} className="p-2 border rounded" />
              <input required name="checkOut" type="date" value={form.checkOut} onChange={(e)=>setForm(p=>({...p, checkOut:e.target.value}))} className="p-2 border rounded" />
            </div>
            <input name="guests" type="number" min="1" max="10" value={form.guests} onChange={(e)=>setForm(p=>({...p, guests:e.target.value}))} className="w-full p-2 border rounded" />
            <div className="flex gap-2">
              <button type="submit" className="flex-1 bg-green-700 text-white py-2 rounded">Confirm</button>
              <button type="button" onClick={()=>setSelectedStay(null)} className="flex-1 border border-gray-300 py-2 rounded">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/* 2. Farm Experience Activities: book experience slot */
const FarmExperiencePortlet = () => {
  const activities = [
    { id: 1, name: "Tractor Ride & Farm Tour", duration: "2 hrs", price: 500 },
    { id: 2, name: "Beekeeping Workshop", duration: "3 hrs", price: 800 },
    { id: 3, name: "Organic Compost Making", duration: "2 hrs", price: 300 },
  ];
  const [cart, setCart] = useState([]);
  const addToCart = (act) => setCart((c) => [...c, act]);
  const removeFromCart = (index) => setCart((c)=> c.filter((_,i)=>i!==index));
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4">
        {activities.map((a) => (
          <div key={a.id} className="p-3 bg-white rounded-lg border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{a.name}</h4>
                <p className="text-sm text-gray-500">{a.duration}</p>
              </div>
              <div className="text-green-700 font-semibold">
                <FontAwesomeIcon icon={faRupeeSign} /> {a.price}
              </div>
            </div>
            <button className="mt-3 px-3 py-1 bg-green-600 text-white rounded" onClick={()=>addToCart(a)}>Add</button>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-white p-3 rounded border">
        <h5 className="font-semibold">Your Experience Cart</h5>
        {cart.length === 0 ? <p className="text-sm text-gray-500">No activities added.</p> : (
          <ul className="space-y-2">
            {cart.map((c,idx)=>(
              <li key={idx} className="flex justify-between items-center">
                <div>{c.name} <span className="text-sm text-gray-500">({c.duration})</span></div>
                <div className="flex items-center gap-2">
                  <div className="text-green-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign}/> {c.price}</div>
                  <button onClick={()=>removeFromCart(idx)} className="text-sm text-red-500">Remove</button>
                </div>
              </li>
            ))}
            <li className="text-right font-semibold">Total: <span className="text-green-700">{cart.reduce((s,i)=>s+i.price,0)}</span></li>
            <li><button className="w-full bg-blue-600 text-white py-2 rounded">Checkout (Dummy)</button></li>
          </ul>
        )}
      </div>
    </div>
  );
};

/* 3. Agri Festivals & Events: list events + register */
const FestivalsPortlet = () => {
  const events = [
    { id: 1, title: "Harvest Festival", date: "2025-11-03", place: "Rural Fairground", price: 200, seats: 50 },
    { id: 2, title: "Village Music Night", date: "2025-12-12", place: "Community Barn", price: 150, seats: 80 },
  ];
  const [regs, setRegs] = useState([]);
  const register = (ev) => {
    if (ev.seats <= regs.filter(r=>r.eventId===ev.id).length) { alert("Sold out"); return; }
    const name = prompt("Your name for registration (dummy):");
    if (!name) return;
    setRegs((r)=>[...r, { eventId: ev.id, name }]);
    alert(`Registered ${name} for ${ev.title}`);
  };
  return (
    <div>
      <div className="space-y-3">
        {events.map((ev)=>(
          <div key={ev.id} className="bg-white p-3 rounded border flex justify-between items-start">
            <div>
              <h4 className="font-semibold">{ev.title}</h4>
              <p className="text-sm text-gray-500">{ev.date} • {ev.place}</p>
            </div>
            <div className="text-right">
              <div className="text-green-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign}/> {ev.price}</div>
              <button onClick={()=>register(ev)} className="mt-2 bg-green-600 text-white px-3 py-1 rounded">Register</button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-white p-3 rounded border">
        <h5 className="font-semibold">Registrations (dummy)</h5>
        {regs.length === 0 ? <p className="text-sm text-gray-500">No registrations yet.</p> :
          regs.map((r,idx)=>(
            <div key={idx} className="text-sm">{r.name} → {events.find(e=>e.id===r.eventId).title}</div>
          ))
        }
      </div>
    </div>
  );
};

/* 4. Farm-to-Table Dining: menu & booking */
const DiningPortlet = () => {
  const menus = [
    { id:1, name:"Village Thali", price:350, desc:"Seasonal vegetables, rotis, chutneys."},
    { id:2, name:"Organic Breakfast", price:200, desc:"Pancakes with organic honey & fruits."},
  ];
  const [orders, setOrders] = useState([]);
  const order = (m)=> {
    const qty = Number(prompt("Quantity (dummy):", "1"));
    if (!qty || qty <= 0) return;
    setOrders(o => [...o, {menu:m, qty}]);
    alert(`Ordered ${qty} x ${m.name} (dummy)`);
  };
  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        {menus.map(m=>(
          <div key={m.id} className="bg-white p-3 rounded border">
            <h4 className="font-semibold">{m.name}</h4>
            <p className="text-sm text-gray-600">{m.desc}</p>
            <div className="mt-2 flex justify-between items-center">
              <div className="text-green-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign}/> {m.price}</div>
              <button onClick={()=>order(m)} className="bg-green-600 text-white px-3 py-1 rounded">Order</button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-white p-3 rounded border">
        <h5 className="font-semibold">Orders (dummy)</h5>
        {orders.length === 0 ? <p className="text-sm text-gray-500">No orders yet.</p> :
          orders.map((o,idx)=>(
            <div key={idx} className="flex justify-between text-sm py-1 border-b">
              <div>{o.qty} x {o.menu.name}</div>
              <div><FontAwesomeIcon icon={faRupeeSign}/> {o.qty*o.menu.price}</div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

/* 5. Educational Tours (school bookings) */
const EducationPortlet = () => {
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({institution:"", date:"", students:20, contact:""});

  const submit = (e)=>{
    e.preventDefault();
    setRequests(r=>[...r, {...form, id:Date.now()}]);
    setForm({institution:"", date:"", students:20, contact:""});
    alert("Request submitted (dummy). Organizer will contact you.");
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-white p-3 rounded border space-y-3">
        <input required placeholder="School / College name" className="w-full p-2 border rounded" value={form.institution} onChange={(e)=>setForm({...form,institution:e.target.value})} />
        <div className="grid grid-cols-2 gap-2">
          <input required type="date" className="p-2 border rounded" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} />
          <input required type="number" className="p-2 border rounded" value={form.students} min="1" onChange={(e)=>setForm({...form,students:e.target.value})} />
        </div>
        <input required placeholder="Contact phone / email" className="w-full p-2 border rounded" value={form.contact} onChange={(e)=>setForm({...form,contact:e.target.value})}/>
        <button className="w-full bg-green-600 text-white py-2 rounded">Request Educational Tour</button>
      </form>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Submitted Requests</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No requests yet.</p> :
          requests.map(r=>(
            <div key={r.id} className="text-sm">{r.institution} • {r.date} • {r.students} students</div>
          ))
        }
      </div>
    </div>
  );
};

/* 6. Adventure & Wellness retreat bookings */
const WellnessPortlet = () => {
  const retreats = [
    {id:1, title:"Yoga Retreat Weekend", price:6000, days:3},
    {id:2, title:"Nature Trek & Wellness", price:4500, days:2},
  ];
  const [bookings, setBookings] = useState([]);
  const book = (r)=> {
    const name = prompt("Your name (dummy):");
    if (!name) return;
    setBookings(b=>[...b, {id:Date.now(), name, retreat:r}]);
    alert(`Booked ${r.title} (dummy)`);
  };
  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        {retreats.map(r=>(
          <div key={r.id} className="bg-white p-3 rounded border">
            <h4 className="font-semibold">{r.title}</h4>
            <p className="text-sm text-gray-500">{r.days} days • <FontAwesomeIcon icon={faRupeeSign}/> {r.price}</p>
            <button onClick={()=>book(r)} className="mt-2 bg-green-600 text-white px-3 py-1 rounded">Book</button>
          </div>
        ))}
      </div>
      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Bookings</h5>
        {bookings.length===0 ? <p className="text-sm text-gray-500">No bookings yet.</p> : bookings.map(b=>(<div key={b.id} className="text-sm">{b.name} → {b.retreat.title}</div>))}
      </div>
    </div>
  );
};

/* 7. Souvenir Shop (mini cart) */
const ShopPortlet = () => {
  const items = [
    {id:1, name:"Raw Honey (250g)", price:250},
    {id:2, name:"Handmade Jute Bag", price:399},
    {id:3, name:"Organic Spice Mix", price:199},
  ];
  const [cart, setCart] = useState([]);
  const add=(it)=>setCart(c=>[...c,it]);
  const remove=(i)=>setCart(c=>c.filter((_,idx)=>idx!==i));
  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map(it=>(
          <div key={it.id} className="bg-white p-3 rounded border flex justify-between items-center">
            <div>
              <div className="font-medium">{it.name}</div>
              <div className="text-sm text-gray-500"><FontAwesomeIcon icon={faRupeeSign}/> {it.price}</div>
            </div>
            <button onClick={()=>add(it)} className="bg-green-600 text-white px-3 py-1 rounded">Add</button>
          </div>
        ))}
      </div>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Cart</h5>
        {cart.length===0 ? <p className="text-sm text-gray-500">Cart empty.</p> : (
          <>
            {cart.map((c, idx)=>(
              <div key={idx} className="flex justify-between items-center py-1">
                <div>{c.name}</div>
                <div className="flex items-center gap-3">
                  <div className="text-green-700"><FontAwesomeIcon icon={faRupeeSign}/> {c.price}</div>
                  <button onClick={()=>remove(idx)} className="text-red-500 text-sm">Remove</button>
                </div>
              </div>
            ))}
            <div className="text-right mt-2 font-semibold">Total: <span className="text-green-700">{cart.reduce((s,i)=>s+i.price,0)}</span></div>
            <button className="mt-2 w-full bg-blue-600 text-white py-2 rounded">Checkout (dummy)</button>
          </>
        )}
      </div>
    </div>
  );
};

/* 8. Farmer & Tourism Leads (inquiries) */
const LeadsPortlet = () => {
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({name:"", contact:"", need:"", details:""});

  const submit = (e) => {
    e.preventDefault();
    setLeads(l=>[...l,{...form, id:Date.now()}]);
    setForm({name:"", contact:"", need:"", details:""});
    alert("Lead submitted (dummy)");
  };
  return (
    <div>
      <form onSubmit={submit} className="bg-gray-600 p-3 rounded border space-y-2">
        <input required placeholder="Your name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="w-full p-2 border rounded" />
        <input required placeholder="Contact (phone / email)" value={form.contact} onChange={(e)=>setForm({...form, contact:e.target.value})} className="w-full p-2 border rounded" />
        <select required value={form.need} onChange={(e)=>setForm({...form, need:e.target.value})} className="w-full p-2 border rounded">
          <option value="">Select need</option>
          <option>Host Farm Stay</option>
          <option>Offer Activity</option>
          <option>List Product (Souvenir)</option>
          <option>Organize Event</option>
        </select>
        <textarea placeholder="Details" value={form.details} onChange={(e)=>setForm({...form, details:e.target.value})} className="w-full p-2 border rounded" />
        <button className="w-full bg-green-600 text-white py-2 rounded">Submit Lead</button>
      </form>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Leads (dummy)</h5>
        {leads.length === 0 ? <p className="text-sm text-gray-500">No leads yet.</p> : leads.map(l=>(
          <div key={l.id} className="text-sm border-b py-1">{l.name} • {l.need} • {l.contact}</div>
        ))}
      </div>
    </div>
  );
};

/* ----------------- Hub Container ----------------- */

export default function AgriLinkAgriTourismHub() {
  const [active, setActive] = useState(null);

  const useCases = [
    { id: "farmstay", title: "Farm Stays", desc: "Book authentic farm stays & cottages", icon: faHome, component: <FarmStayBookingPortlet /> },
    { id: "activities", title: "Farm Experiences", desc: "Book workshops & hands-on farm activities", icon: faLeaf, component: <FarmExperiencePortlet /> },
    { id: "festivals", title: "Festivals & Events", desc: "Seasonal fairs, harvest celebrations & tickets", icon: faCalendarAlt, component: <FestivalsPortlet /> },
    { id: "dining", title: "Farm-to-Table Dining", desc: "Local culinary experiences & bookings", icon: faUtensils, component: <DiningPortlet /> },
    { id: "education", title: "Educational Tours", desc: "School & institution tours and learning journeys", icon: faGraduationCap, component: <EducationPortlet /> },
    { id: "wellness", title: "Adventure & Wellness", desc: "Yoga retreats, treks & wellness packages", icon: faSpa, component: <WellnessPortlet /> },
    { id: "shop", title: "Souvenir Shop", desc: "Buy local produce & handmade goods", icon: faShoppingBasket, component: <ShopPortlet /> },
    { id: "leads", title: "Farmer & Tourism Leads", desc: "Manage host & tourism leads for farmers", icon: faHeadset, component: <LeadsPortlet /> },
  ];

  return (
    <div>
    <section className="min-h-screen bg-gradient-to-r from-green-700 to-green-800 py-12 px-4 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <header className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-yellow-200 flex items-center justify-center gap-3">
            <span className="text-3xl bg-green-100 text-yellow-200 rounded-full w-12 h-12 flex items-center justify-center">🌾</span>
            AgriLink AgriTourism
          </h1>
          <p className="mt-3 text-white max-w-3xl mx-auto">Discover, book and host farm stays, experiences, festivals and more — sustainable tourism that benefits local communities and farmers.</p>
        </header>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {useCases.map((u) => (
            <Portlet
              key={u.id}
              icon={u.icon}
              title={u.title}
              desc={u.desc}
              expanded={active === u.id}
              onToggle={() => setActive((s) => (s === u.id ? null : u.id))}
            >
              {/* component area */}
              {u.component}
              {/* small footer area for this portlet: quick meta */}
              {/* <div className="mt-4 text-xs text-gray-500">Tip: Click any card to expand.</div> */}
            </Portlet>
          ))}
        </div>
      </div>
    </section>
    {/* Footer */}
    <footer className="bg-green-700 text-white py-10 px-4 sm:px-6 md:px-20">
      <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
        <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
        <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
          <Link to="/about-us" className="text-white hover:text-green-400 transition">About</Link>
          <Link to="/privacy-policy" className="text-white hover:text-green-400 transition">Privacy Policy</Link>
          <Link to="/terms-of-use" className="text-white hover:text-green-400 transition">Terms of Use</Link>
          <Link to="/contact-us" className="text-white hover:text-green-400 transition">Contact</Link>
        </div>
      </div>
    </footer>
  </div>
  );
}
